// Fixed-position tooltip pill for the collapsed-sidebar rail, rendered in a
// portal so the rail nav can scroll vertically (overflow-y-auto) without
// clipping tooltips — CSS attr()/::after tooltips cannot survive a scrolling
// ancestor because overflow-x cannot stay visible once overflow-y scrolls.
// Shows after a 250ms hover-intent delay, immediately on keyboard focus
// (focus/blur use the capture phase so wrapped interactive children — e.g.
// PaletteTrigger — trigger it without prop changes). Dismissed on scroll.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

const HOVER_INTENT_MS = 250;
const GAP_PX = 12;

export interface RailTooltipProps {
  label: string;
  children: ReactNode;
}

export function RailTooltip({ label, children }: RailTooltipProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  // React 19 types removed the zero-arg useRef overload — initialise with null.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const show = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ top: rect.top + rect.height / 2, left: rect.right + GAP_PX });
  }, []);

  const hide = useCallback(() => {
    clearTimer();
    setPos(null);
  }, [clearTimer]);

  const onMouseEnter = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(show, HOVER_INTENT_MS);
  }, [clearTimer, show]);

  useEffect(() => {
    if (!pos) return;
    window.addEventListener("scroll", hide, true);
    return () => window.removeEventListener("scroll", hide, true);
  }, [pos, hide]);

  useEffect(() => clearTimer, [clearTimer]);

  return (
    <span
      ref={anchorRef}
      className="flex"
      onMouseEnter={onMouseEnter}
      onMouseLeave={hide}
      onFocusCapture={show}
      onBlurCapture={hide}
    >
      {children}
      {pos &&
        createPortal(
          <div
            role="tooltip"
            style={{ top: pos.top, left: pos.left }}
            className="pointer-events-none fixed z-50 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1 text-caption font-medium text-white shadow-soft-lg dark:shadow-soft-lg-dark dark:border dark:border-slate-700 animate-fade-in [animation-duration:120ms] motion-reduce:animate-none"
          >
            {label}
          </div>,
          document.body,
        )}
    </span>
  );
}
